import React, {useCallback, useState} from 'react';
import {Box, Dialog as MuiDialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import Button from '../../../../../../../components/base/button/button';
import Form, {MuiRFF} from '../../../../../../../components/form';
import Grid2 from '@mui/material/Unstable_Grid2';
import * as Yup from 'yup';
import ValidateMessages from '../../../../../../../../core/models/static/texts/vallidate-messages';
import useIsMounted from '../../../../../../../hooks/use-is-mounted';
import {DashboardEntry} from "../../types";
import Api from "../../../../../../../../core/services/api/api";
import {useOperatorInformation} from "../../../../../../../hooks/use-operator-information";

interface CreateDashboardDialogProps {
    open: boolean;
    onClose: (dashboard?: DashboardEntry) => void;
}

const formNames = {
    name: 'name'
};

const schema = Yup.object().shape({
    [formNames.name]: Yup.string().trim().required(ValidateMessages.required)
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const CreateDashboardDialog = ({open, onClose}: CreateDashboardDialogProps) => {
    const [creating, setCreating] = useState(false);
    const isMounted = useIsMounted();
    const operatorInformation = useOperatorInformation();

    /**
     * Handles creating a new dashboard
     */
    const handleSubmit = useCallback(async (values: any) => {
        setCreating(true);
        const response = await Api.createCustomDashboard({
            title: values[formNames.name],
        });
        if (!isMounted())
            return;
        setCreating(false);
        if (response?.resultFlag) {
            const dashboard = {
                id: response.data.id,
                title: response.data.title,
                isDefault: false,
                ownerName: operatorInformation?.name ?? "",
                isOwnedByMe: true,
                uiComponentData: '',
                newWidgets: [],
                groupIds: []
            } as DashboardEntry;
            onClose(dashboard);
        }
    }, [isMounted, onClose, operatorInformation?.name]);

    /**
     * Handles closing the dialog without saving
     */
    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    return (
        <MuiDialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Create New Dashboard</DialogTitle>
            <Form
                onSubmit={handleSubmit}
                validate={validate}
                formProps={{id: 'create-dashboard-form'}}
            >
                <>
                    <DialogContent dividers sx={{paddingBlock: 5}}>
                        <Grid2 container spacing={2}>
                            <Grid2 xs={12}>
                                <MuiRFF.TextField
                                    name={formNames.name}
                                    label="Dashboard Name"
                                    required={required[formNames.name]}
                                    fullWidth
                                />
                            </Grid2>
                        </Grid2>
                    </DialogContent>
                    <DialogActions className="p-0">
                        <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                            <Button
                                disabled={creating}
                                variant="dialog"
                                color="primary"
                                onClick={handleClose}
                                sx={{borderRadius: '0 10px 0 10px'}}
                            >
                                Close
                            </Button>
                            <Button
                                disabled={creating}
                                variant="dialog"
                                color="primary"
                                type="submit"
                                form="create-dashboard-form"
                                sx={{borderRadius: '10px 0 10px 0'}}
                            >
                                {creating ? "Creating..." : "Create"}
                            </Button>
                        </Box>
                    </DialogActions>
                </>
            </Form>
        </MuiDialog>
    );
};

export default CreateDashboardDialog; 